import { useEffect, useState } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AffiliateStrip — fetches active partners for a placement and renders them
 * as a horizontal row of compact cards. Click tracking goes through
 * /api/partners/{id}/click (logs + redirects).
 *
 * Props:
 *   placement   — "homepage" | "cardio_finish" | "profile_sidebar" | "shop" | "my_corner"
 *   title       — optional section heading (e.g. "Partners & Picks")
 *   variant     — "row"      : compact 3-col grid (default)
 *                 "stack"    : tall vertical stack (sidebars / narrow areas)
 *                 "feature"  : large single-column hero cards w/ big imagery + Roc's note
 *                 "carousel" : auto-cycling hero — big feature on md+, compact on mobile.
 *                              8s advance, pauses on hover/focus. Chevron + dot controls.
 */
export default function AffiliateStrip({ placement, title = "Partners & Picks", variant = "row" }) {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [carIdx, setCarIdx] = useState(0);
  const [carPaused, setCarPaused] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/partners?placement=${encodeURIComponent(placement)}`);
        if (!cancelled) setPartners(data?.partners || []);
      } catch {
        if (!cancelled) setPartners([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [placement]);

  // 🎠 Carousel auto-advance — only ticks for variant=carousel with multiple partners,
  // and pauses when the user hovers or focuses the carousel region.
  useEffect(() => {
    if (variant !== "carousel" || partners.length <= 1 || carPaused) return;
    const t = setInterval(() => {
      setCarIdx((i) => (i + 1) % partners.length);
    }, 8000);
    return () => clearInterval(t);
  }, [variant, partners.length, carPaused]);

  // Defensive: keep carIdx in range when partners list shrinks
  useEffect(() => {
    if (carIdx >= partners.length && partners.length > 0) setCarIdx(0);
  }, [partners.length, carIdx]);

  async function handleClick(p) {
    try {
      const { data } = await axios.post(`${API}/partners/${p.partner_id}/click`);
      if (data?.url) window.open(data.url, "_blank", "noopener,noreferrer");
    } catch {
      // Silent fail — affiliate link is meant to open regardless
    }
  }

  if (loading || partners.length === 0) return null;

  // ---------- shared card renderers ----------
  const FeatureCard = (p) => (
    <button
      key={p.partner_id}
      data-testid={`partner-card-${p.partner_id}`}
      onClick={() => handleClick(p)}
      className="block w-full text-left bg-[#0F0F0F] border-2 border-zinc-800 hover:border-[#D4AF37] transition-colors p-5 md:p-7 group"
    >
      <div className="flex flex-col md:flex-row gap-6 items-center md:items-start">
        {p.image_url && (
          <div className="flex-shrink-0 w-full md:w-72 h-72 bg-black border border-zinc-900 overflow-hidden">
            <img
              src={p.image_url}
              alt={p.product_name}
              className="w-full h-full object-contain group-hover:scale-105 transition-transform"
              loading="lazy"
            />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
              {p.partner_name}
            </span>
            {p.roc_approved && (
              <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 bg-[#D4AF37]/20 text-[#D4AF37] font-bold">
                ★ Roc Pick
              </span>
            )}
          </div>
          <div className="font-display text-3xl md:text-4xl text-white leading-tight mb-2">
            {p.product_name}
          </div>
          {p.tagline && (
            <div className="text-sm md:text-base text-neutral-300 mb-3">
              {p.tagline}
            </div>
          )}
          {p.roc_note && (
            <div className="text-sm text-neutral-400 italic border-l-2 border-[#D4AF37]/60 pl-3 my-4">
              “{p.roc_note}”
            </div>
          )}
          {p.discount_code && (
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-widest text-neutral-500">Use code:</span>
              <span className="font-mono text-base tabular-nums px-3 py-1 bg-[#D4AF37] text-black font-bold tracking-wider">
                {p.discount_code}
              </span>
              {p.discount_text && (
                <span className="text-xs uppercase tracking-widest text-neutral-400">
                  {p.discount_text}
                </span>
              )}
            </div>
          )}
          <div className="mt-5 inline-flex items-center gap-1 text-sm text-[#D4AF37] font-bold uppercase tracking-widest group-hover:text-white transition-colors">
            Shop →
          </div>
        </div>
      </div>
    </button>
  );

  const CompactCard = (p) => (
    <button
      key={p.partner_id}
      data-testid={`partner-card-${p.partner_id}`}
      onClick={() => handleClick(p)}
      className="w-full text-left bg-[#0F0F0F] border-2 border-zinc-800 hover:border-[#D4AF37] transition-colors p-4 flex gap-4 group"
    >
      {p.image_url && (
        <div className="flex-shrink-0 w-20 h-20 bg-black border border-zinc-900 overflow-hidden">
          <img
            src={p.image_url}
            alt={p.product_name}
            className="w-full h-full object-contain group-hover:scale-105 transition-transform"
            loading="lazy"
          />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold">
            {p.partner_name}
          </span>
          {p.roc_approved && (
            <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-[#D4AF37]/20 text-[#D4AF37] font-bold">
              ★ Roc Pick
            </span>
          )}
        </div>
        <div className="font-display text-base md:text-lg text-white leading-tight truncate">
          {p.product_name}
        </div>
        {p.tagline && (
          <div className="text-xs text-neutral-400 mt-0.5 line-clamp-2">{p.tagline}</div>
        )}
        {p.discount_code && (
          <div className="mt-2 inline-flex items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-widest text-neutral-500">Code:</span>
            <span className="font-mono text-xs tabular-nums px-2 py-0.5 bg-[#D4AF37] text-black font-bold">
              {p.discount_code}
            </span>
            {p.discount_text && (
              <span className="text-[10px] uppercase tracking-widest text-neutral-400">
                · {p.discount_text}
              </span>
            )}
          </div>
        )}
      </div>
    </button>
  );

  // ---------- carousel mode ----------
  if (variant === "carousel") {
    const current = partners[Math.min(carIdx, partners.length - 1)];
    const multi = partners.length > 1;
    const goPrev = () => setCarIdx((i) => (i - 1 + partners.length) % partners.length);
    const goNext = () => setCarIdx((i) => (i + 1) % partners.length);
    return (
      <section
        data-testid={`affiliate-strip-${placement}`}
        className="py-12 md:py-16 bg-[#0A0A0A]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-baseline justify-between mb-5">
            <h3 className="font-display text-2xl md:text-3xl text-white">{title}</h3>
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold">
              🤝 Roc-Approved Partners
            </div>
          </div>

          <div
            className="relative"
            data-testid={`affiliate-carousel-${placement}`}
            onMouseEnter={() => setCarPaused(true)}
            onMouseLeave={() => setCarPaused(false)}
            onFocus={() => setCarPaused(true)}
            onBlur={() => setCarPaused(false)}
          >
            {/* Slide — fade in/out on index change via key remount */}
            <div key={current.partner_id} className="animate-affiliate-fade">
              <div className="hidden md:block">{FeatureCard(current)}</div>
              <div className="md:hidden">{CompactCard(current)}</div>
            </div>

            {multi && (
              <>
                <button
                  type="button"
                  onClick={goPrev}
                  aria-label="Previous partner"
                  data-testid={`affiliate-carousel-prev-${placement}`}
                  className="absolute top-1/2 -translate-y-1/2 -left-2 sm:-left-4 w-9 h-9 sm:w-10 sm:h-10 bg-black/80 border border-[#D4AF37]/60 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-colors flex items-center justify-center"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  aria-label="Next partner"
                  data-testid={`affiliate-carousel-next-${placement}`}
                  className="absolute top-1/2 -translate-y-1/2 -right-2 sm:-right-4 w-9 h-9 sm:w-10 sm:h-10 bg-black/80 border border-[#D4AF37]/60 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-colors flex items-center justify-center"
                >
                  <ChevronRight size={18} />
                </button>
              </>
            )}

            {multi && (
              <div className="flex items-center justify-center gap-2 mt-4">
                {partners.map((p, i) => (
                  <button
                    key={p.partner_id}
                    type="button"
                    aria-label={`Show partner ${i + 1}`}
                    onClick={() => setCarIdx(i)}
                    data-testid={`affiliate-carousel-dot-${placement}-${i}`}
                    className={
                      "h-1.5 transition-all " +
                      (i === carIdx
                        ? "w-8 bg-[#D4AF37]"
                        : "w-3 bg-zinc-700 hover:bg-zinc-500")
                    }
                  />
                ))}
              </div>
            )}
          </div>

          <div className="text-[10px] uppercase tracking-widest text-neutral-600 text-center mt-4">
            Disclosure: OmniMuscle may earn a commission when you shop through these links — at no extra cost to you.
          </div>
        </div>
      </section>
    );
  }

  // ---------- default modes ----------
  const isFeature = variant === "feature";
  return (
    <section
      data-testid={`affiliate-strip-${placement}`}
      className="py-12 md:py-16 bg-[#0A0A0A]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-baseline justify-between mb-5">
          <h3 className="font-display text-2xl md:text-3xl text-white">{title}</h3>
          <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold">
            🤝 Roc-Approved Partners
          </div>
        </div>
        <div className={
          isFeature
            ? "space-y-6"
            : variant === "stack"
            ? "space-y-3"
            : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        }>
          {partners.map((p) => (isFeature ? FeatureCard(p) : CompactCard(p)))}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-600 text-center mt-4">
          Disclosure: OmniMuscle may earn a commission when you shop through these links — at no extra cost to you.
        </div>
      </div>
    </section>
  );
}
